import {DuplicateIcon} from '@heroicons/react/outline';

import {Badge, Button, SpinningLoader} from '@/components';
import type {
  WebhookDeliveryAttempt,
  WebhookDeliveryStatus,
} from '@/types/WebhookDelivery';
import {dateTimeInTimeZone} from '@/utils/date';
import {copyToClipboard} from '@/utils/user';

import type {AttemptCardProps, WebhookDeliveryDetailProps} from './types';
import useWebhookDeliveryDetail from './useWebhookDeliveryDetail';

const PRE_CLASS =
  'p-3 bg-gray-100 dark:bg-dark-inputBg rounded border border-gray-200 dark:border-dark-borderMid overflow-x-auto whitespace-pre-wrap break-words text-xs flex-1';

// `request` and `response.body` are unknown — the latter is arbitrary content
// from the customer's server. Always render as text via JSON.stringify inside
// <pre> (React auto-escapes). Never dangerouslySetInnerHTML.
const stringify = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// Success signal is `error === null` (per the server contract): response.status
// is absent for echoes / transport cases, so a missing code with no error is a
// success, not a failure. A transport error => FAILED; a returned non-2xx/3xx
// code => FAILED; everything else => SUCCESS.
const statusOf = (attempt: WebhookDeliveryAttempt): WebhookDeliveryStatus => {
  if (attempt.error) {
    return 'FAILED';
  }
  const status = attempt.response?.status;
  if (status != null) {
    const code = Number(status);
    return code >= 200 && code < 400 ? 'SUCCESS' : 'FAILED';
  }
  return 'SUCCESS';
};

const formatTimestampOrDash = (ts?: string): string =>
  ts ? dateTimeInTimeZone(ts) : '—';

const AttemptCard = ({attempt}: AttemptCardProps) => {
  const status = statusOf(attempt);
  const responseBody = attempt.response?.body;
  return (
    <div className="border border-gray-200 dark:border-dark-borderMid rounded-lg p-3 space-y-2 bg-white dark:bg-transparent">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Attempt #{attempt.attempt ?? '—'}
          </span>
          <Badge
            colorScheme={status === 'SUCCESS' ? 'successful' : 'failed'}
            label={status}
          />
          {attempt.response?.status && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              HTTP {attempt.response.status}
            </span>
          )}
        </div>
        <span className="text-sm text-gray-400 dark:text-gray-500">
          {formatTimestampOrDash(attempt.timestamp)}
        </span>
      </div>

      {attempt.error && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-red-600 dark:text-red-400">
            {attempt.error.name}
          </p>
          <p className="text-sm text-red-500 dark:text-red-300">
            {attempt.error.message}
          </p>
        </div>
      )}

      {responseBody != null && (
        <div className="space-y-1">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Response body
          </p>
          <div className="flex items-start gap-2">
            <pre className={PRE_CLASS}>{stringify(responseBody)}</pre>
            <Button
              variant="unStyled"
              onClick={() => copyToClipboard(stringify(responseBody))}
              title="Copy to clipboard"
            >
              <DuplicateIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const WebhookDeliveryDetail = ({
  businessId,
  webhookId,
}: WebhookDeliveryDetailProps) => {
  const {detail, isLoading, error, isNotFound} = useWebhookDeliveryDetail(
    businessId,
    webhookId,
  );

  if (!webhookId) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full py-16 text-center text-gray-500 dark:text-gray-400"
        data-testid="detail-empty-selection"
      >
        <p>Select a delivery to inspect its payload and attempts.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        data-testid="detail-loading"
      >
        <SpinningLoader size={8} />
      </div>
    );
  }

  if (isNotFound) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-center text-gray-500 dark:text-gray-400"
        data-testid="detail-not-found"
      >
        <p>
          Delivery not found. It may have expired or belongs to another
          business.
        </p>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-center text-red-600 dark:text-red-400"
        data-testid="detail-error"
      >
        <p>Failed to load delivery detail. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="delivery-detail">
      <div className="space-y-2">
        {detail.event && (
          <p className="font-mono font-bold text-gray-900 dark:text-gray-100">
            {detail.event}
          </p>
        )}
        {detail.url && (
          <p className="text-sm text-gray-500 dark:text-gray-400 break-all">
            {detail.url}
          </p>
        )}
        {!detail.attempted && (
          <Badge colorScheme="pending" label="Queued — not yet attempted" />
        )}
      </div>

      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Request payload
        </p>
        <div className="flex items-start gap-2">
          <pre className={PRE_CLASS} data-testid="detail-request">
            {stringify(detail.request)}
          </pre>
          <Button
            variant="unStyled"
            onClick={() => copyToClipboard(stringify(detail.request))}
            title="Copy to clipboard"
          >
            <DuplicateIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Attempts
        </p>
        {detail.attempts.length === 0 ? (
          <p
            className="text-sm text-gray-400 dark:text-gray-500"
            data-testid="detail-no-attempts"
          >
            Detailed attempt history is not yet available for this delivery.
          </p>
        ) : (
          <div className="space-y-3">
            {detail.attempts.map((attempt, index) => (
              <AttemptCard key={attempt.attempt ?? index} attempt={attempt} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
